import { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import MembershipStepper from "./MembershipStepper";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import { MESSAGE_TYPES } from "../../../utils/editIframe";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import { handleResetWithValidation } from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Membership", path: `/membership` },
  { title: "Thanks", path: `/membership/thanks` },
];

export default function MembershipThanksEditPage() {
  const contentKey = "membershipContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    prepare_items: [{ name: "", desc: "" }],
    thanks_bank_desc: "",
    thanks_bank_targets: "",
    thanks_first_fee_name: "初回月謝（現金）",
    thanks_first_fee_desc: "初回月謝をご用意ください",
  });
  const [studios, setStudios] = useState([]);

  const postMessageToIframe = useCallback(
    (newData) => {
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: MESSAGE_TYPES.TEXT_UPDATE, contentKey, allData: newData },
          process.env.REACT_APP_ORIGIN,
        );
      }
    },
    [contentKey],
  );

  const fetchStudios = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_studio_key_codes/`);
      if (!response.ok) throw new Error("スタジオ取得失敗");
      const data = await response.json();
      setStudios(data.studios ?? []);
    } catch (error) {
      console.error("スタジオ取得エラー:", error);
    }
  }, []);

  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_page_membership_content/`,
      );
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      if (!data.prepare_items?.length)
        data.prepare_items = [{ name: "", desc: "" }];
      setPageContent(data);
    } catch (error) {
      console.error("データ取得エラー:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchStudios();
  }, [fetchPageContentData, fetchStudios]);

  const handleKeyCodeChange = (studioId, value) => {
    setStudios((prev) =>
      prev.map((s) => (s.id === studioId ? { ...s, key_code: value } : s)),
    );
  };

  const handleBankFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        { type: MESSAGE_TYPES.SCROLL_TO, target: ".thanks-bank", contentKey },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const handleBankChange = (fieldName, value) => {
    setPageContent((prev) => {
      const newData = { ...prev, [fieldName]: value };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handlePrepareFocus = () => {
    const iframe = document.querySelector("iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.SCROLL_TO,
          target: ".thanks-prepare-list",
          contentKey,
        },
        process.env.REACT_APP_ORIGIN,
      );
    }
  };

  const handlePrepareInputChange = (index, fieldName, value) => {
    setPageContent((prev) => {
      const newItems = prev.prepare_items.map((item, i) =>
        i === index ? { ...item, [fieldName]: value } : item,
      );
      const newData = { ...prev, prepare_items: newItems };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const addPrepareItem = () => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        prepare_items: [...prev.prepare_items, { name: "", desc: "" }],
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const removePrepareItem = (index) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        prepare_items:
          prev.prepare_items.length <= 1
            ? [{ name: "", desc: "" }]
            : prev.prepare_items.filter((_, i) => i !== index),
      };
      postMessageToIframe(newData);
      return newData;
    });
  };

  const handlePageDataSubmit = async () => {
    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const [contentResponse] = await Promise.all([
        fetch(`${apiUrl}/api/update_page_membership_content/`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ page_content: pageContent }),
        }),
        ...studios.map((s) =>
          fetch(`${apiUrl}/api/update_studio_key_code/`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ studio_id: s.id, key_code: s.key_code }),
          }),
        ),
      ]);
      const data = await contentResponse.json();
      if (!contentResponse.ok) throw new Error(data.message);
      setMessage(data.message);
      await Promise.all([fetchPageContentData(), fetchStudios()]);
      setTimeout(() => {
        const editArea = document.querySelector(".editarea");
        if (editArea) editArea.scrollTop = 0;
      }, 100);
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      setMessage("更新に失敗しました");
      setTimeout(() => setMessage(""), 3000);
    }
    setLoadingText("");
  };

  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />
            <MembershipStepper active="thanks" />

            <div className="form">
              <section>
                <h4 className="section-title">準備</h4>
                {/* 初回月謝（固定・削除不可・文言のみ編集）。website 側で条件表示・金額自動差し込みのため準備リストとは別管理 */}
                <dl className="information txt-form">
                  <div style={{ display: "contents" }}>
                    <dt>
                      <input
                        type="text"
                        name="first_fee_name"
                        className="q"
                        value={pageContent.thanks_first_fee_name ?? ""}
                        onChange={(e) =>
                          handleBankChange(
                            "thanks_first_fee_name",
                            e.target.value,
                          )
                        }
                        onFocus={handlePrepareFocus}
                        placeholder="初回月謝の見出し（例：初回月謝（現金））"
                      />
                    </dt>
                    <dd className="no-remove">
                      <input
                        type="text"
                        name="first_fee_desc"
                        className="q"
                        value={pageContent.thanks_first_fee_desc ?? ""}
                        onChange={(e) =>
                          handleBankChange(
                            "thanks_first_fee_desc",
                            e.target.value,
                          )
                        }
                        onFocus={handlePrepareFocus}
                        placeholder="初回月謝の説明（例：初回月謝をご用意ください）"
                      />
                    </dd>
                  </div>
                  {pageContent.prepare_items?.map((item, index) => (
                    <div key={index} style={{ display: "contents" }}>
                      <dt>
                        <input
                          type="text"
                          name={`prepare${index}_name`}
                          className="q"
                          value={item.name ?? ""}
                          onChange={(e) =>
                            handlePrepareInputChange(
                              index,
                              "name",
                              e.target.value,
                            )
                          }
                          onFocus={handlePrepareFocus}
                          placeholder="タイトル（例：お持ち物）"
                        />
                      </dt>
                      <dd
                        onClick={(e) => {
                          if (e.target.tagName !== "INPUT")
                            removePrepareItem(index);
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="text"
                          name={`prepare${index}_desc`}
                          className="q"
                          value={item.desc ?? ""}
                          onChange={(e) =>
                            handlePrepareInputChange(
                              index,
                              "desc",
                              e.target.value,
                            )
                          }
                          onFocus={handlePrepareFocus}
                          placeholder="説明（例：動きやすい服装、室内用のシューズ）"
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
                <div className="accent-wrap">
                  <p onClick={addPrepareItem} style={{ cursor: "pointer" }}>
                    <i className="icon add"></i>
                    <span>Item</span>
                  </p>
                </div>
              </section>
              <section>
                <h4 className="section-title">銀行登録</h4>
                <div className="area3">
                  <div className="item">
                    <input
                      type="text"
                      value={pageContent.thanks_bank_desc ?? ""}
                      onChange={(e) =>
                        handleBankChange("thanks_bank_desc", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="説明文（例：ネットで簡単登録を希望された方は...）"
                    />
                    <input
                      type="text"
                      value={pageContent.thanks_bank_targets ?? ""}
                      onChange={(e) =>
                        handleBankChange("thanks_bank_targets", e.target.value)
                      }
                      onFocus={handleBankFocus}
                      placeholder="対象銀行（例：肥後銀行 / 熊本銀行 / ゆうちょ銀行 / 熊本県信用組合）"
                    />
                  </div>
                </div>
              </section>
              <section>
                <h4 className="section-title">申請用キーコード</h4>
                <div className="area3">
                  <div className="item">
                    {studios.map((studio) => (
                      <div
                        key={studio.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "80px 1fr",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <span style={{ color: "#fff", fontSize: "0.85em" }}>
                          {studio.name}
                        </span>
                        <input
                          type="text"
                          value={studio.key_code ?? ""}
                          onChange={(e) =>
                            handleKeyCodeChange(studio.id, e.target.value)
                          }
                          placeholder="キーコード"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe
        path="/membership/thanks"
        data={pageContent}
        contentKey={contentKey}
      />
    </>
  );
}
